import type { Hotkey, RegisterableHotkey } from "@tanstack/react-hotkeys"

export const HOTKEYS = {
  openSearch: { hotkey: "Mod+K" },
  toggleSidebar: { hotkey: "Mod+'" as Hotkey },
  openHakiAssistant: { hotkey: "Mod+J" },
  // Matched on the typed character, so it works where ? isn't Shift+/ (Nordic layouts: Shift++)
  showShortcuts: { hotkey: { key: "?", shift: true } },
  openFilterPopover: { hotkey: "Mod+Shift+F" },
  focusSearchField: { hotkey: "Mod+F" },
  blurSearchField: { hotkey: "Escape" },
  executeSqlQuery: { hotkey: "Mod+Enter" },
  prevVariant: { hotkey: "Mod+ArrowLeft" },
  nextVariant: { hotkey: "Mod+ArrowRight" },
  exitSettings: { hotkey: "Mod+Escape" },
  // Vim-style row stepping: moves the highlighted row a table's inspector is detailing.
  // Unmodified single keys, so they only fire outside a text field (see isEditableElement).
  prevRow: { hotkey: "K" },
  nextRow: { hotkey: "J" },
  // Inspects the row under the pointer (InspectorPeekCellButton's toggle); with no row hovered,
  // shows/hides the inspector (InspectorToggleButton). Also fires as the I of G I, so the inspector
  // can flip on the way out; G I navigates away anyway — accepted.
  inspectHoveredRow: { hotkey: "I" },
  // Opens the row under the pointer elsewhere (InspectorPeekCellButton's ↗). Shift+O, as
  // REPLENISHMENT_HOTKEYS.openVariant, which defers to it while a row is hovered.
  openHoveredRow: { hotkey: "Shift+O" },
  // Pops one inspector drilldown level. Shares Escape with blurSearchField, which is gated on an
  // expanded search field; this one is gated on an open drilldown and skips editable elements.
  inspectorBack: { hotkey: "Escape" },
  // Drops the inspector's highlighted row. Third claim on Escape, gated on no drilldown being
  // open (inspectorBack owns the key there) and skipping editable elements.
  clearHighlight: { hotkey: "Escape" },
  // Closes the open FullPageDialog. The page behind it keeps its hotkeys mounted, so the claims
  // above stand down while a dialog covers them (useIsCoveredByFullPageDialog).
  closeFullPageDialog: { hotkey: "Escape" },
  // Calendar strip panning. Plain arrows beat the scrollport's own horizontal scrolling, so the
  // handlers preventDefault to step a whole window rather than nudge it a few pixels.
  prevInspectorTab: { hotkey: "ArrowLeft" },
  nextInspectorTab: { hotkey: "ArrowRight" },
  calendarBack: { hotkey: "ArrowLeft" },
  calendarForward: { hotkey: "ArrowRight" },
  calendarToday: { hotkey: "T" },
  // Demand overview list/timeline switch. T shares calendarToday deliberately: from the list it
  // opens the timeline, inside it jumps back to today. T also fires as the second key of G T,
  // which only flips the stored view on the way out to Settings — accepted.
  collectionsListView: { hotkey: "L" },
  collectionsTimelineView: { hotkey: "T" },
} as const satisfies Record<string, { hotkey: RegisterableHotkey }>

export const REPLENISHMENT_HOTKEYS = {
  extendSelectionDown: { hotkeys: ["Shift+J", "Shift+ArrowDown"] },
  extendSelectionUp: { hotkeys: ["Shift+K", "Shift+ArrowUp"] },
  toggleIncluded: { hotkeys: ["X"] },
  toggleDetails: { hotkeys: ["O"] },
  clearOverride: { hotkeys: ["Backspace", "Delete"] },
  toggleChecked: { hotkeys: ["Space"] },
  adjustOverride: { hotkeys: ["A", "Shift+A"] },
  startOverride: {
    hotkeys: [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "-",
      ".",
      "/",
      { key: "+" },
      { key: "+", shift: true },
      { key: "*" },
      { key: "*", shift: true },
      { key: "%", shift: true },
    ],
  },
  closeDetailsOrSelection: { hotkeys: ["Escape"] },
  selectAll: { hotkeys: ["Mod+A"] },
  firstLine: { hotkeys: ["Home"] },
  lastLine: { hotkeys: ["End"] },
  nextWarning: { hotkeys: ["W"] },
  prevWarning: { hotkeys: ["Shift+W"] },
  addToCart: { hotkeys: ["Mod+Enter"] },
  addAllToCart: { hotkeys: ["Mod+Shift+Enter"] },
  openRule: { hotkeys: ["R"] },
  openVariant: { hotkeys: ["Shift+O"] },
} as const satisfies Record<string, { hotkeys: readonly RegisterableHotkey[] }>

/**
 * Saved-view commands for the page's current view, registered by ViewConfiguration. V starts no
 * other sequence and isn't a single-key hotkey; L also fires collectionsListView, which never
 * shares a page with a view bar. Accepted overlaps: the V of G V counts as a start, so on Vendor
 * management (which G V reloads) a quick F/D/L/S right after it fires; and with an editable grid
 * cell focused, V starts editing the cell instead (as with J/K). V C opens the main table's column
 * chooser (useOpenColumnChooserCommand); Mod+Shift+C is taken by Chrome DevTools' element picker.
 */
export const VIEW_SHORTCUTS = {
  favorite: { sequence: ["V", "F"], name: "Favorite or unfavorite view" },
  toggleDefault: { sequence: ["V", "D"], name: "Set or clear default view" },
  copyLink: { sequence: ["V", "L"], name: "Copy link to view" },
  switchView: { sequence: ["V", "S"], name: "Switch view" },
  openColumnChooser: { sequence: ["V", "C"], name: "Open column chooser" },
} as const satisfies Record<string, { sequence: readonly Hotkey[]; name: string }>

export const NAVIGATION_SHORTCUTS = {
  goToHome: { sequence: ["G", "H"] as const, path: "/" },
  goToInbox: { sequence: ["G", "I"] as const, path: "/inbox" },
  goToBudgets: { sequence: ["G", "B"] as const, path: "/budgets" },
  goToFashion: { sequence: ["G", "F"] as const, path: "/demand-planning/fashion" },
  goToNoos: { sequence: ["G", "N"] as const, path: "/demand-planning/noos" },
  goToSupplyPlan: { sequence: ["G", "S"] as const, path: "/procurement/supply-plan" },
  goToPurchaseOrders: { sequence: ["G", "P"] as const, path: "/procurement/purchase-orders" },
  goToIncomingOrders: { sequence: ["G", "O"] as const, path: "/procurement/incoming-orders" },
  goToVendorManagement: { sequence: ["G", "V"] as const, path: "/procurement/vendor-management" },
  goToDemandCalendar: { sequence: ["G", "D"] as const, path: "/insights/forecast-calendar" },
  goToStudio: { sequence: ["G", "U"] as const, path: "/insights/studio" },
  goToAvailability: { sequence: ["G", "A"] as const, path: "/insights/availability" },
  goToReporting: { sequence: ["G", "R"] as const, path: "/reporting" },
  goToProductManagement: { sequence: ["G", "M"] as const, path: "/product-management" },
  goToCollectionManagement: { sequence: ["G", "C"] as const, path: "/collection-management" },
  goToSettings: { sequence: ["G", "T"] as const, path: "/settings" },
} as const satisfies Record<string, { sequence: readonly Hotkey[]; path: string }>

const SEQUENCE_BY_PATH: Record<string, readonly Hotkey[]> = Object.fromEntries(
  Object.values(NAVIGATION_SHORTCUTS).map((s) => [s.path, s.sequence]),
)

/** Where G D points under Helix — the Demand module, whose Sales & Demand tab hosts the page. */
const HELIX_DEMAND_CALENDAR_PATH = "/demand"

/**
 * The navigation shortcuts as they apply to one user. Under Helix the demand calendar is a tab of
 * the Demand module rather than an Insights page, so G D points there instead — consumers gate on
 * the path being in the sidebar, and the Insights one is not.
 */
export function navigationShortcutsFor({ showHelix }: { showHelix: boolean }) {
  if (!showHelix) return Object.values(NAVIGATION_SHORTCUTS)
  return Object.values(NAVIGATION_SHORTCUTS).map((shortcut) =>
    shortcut.path === NAVIGATION_SHORTCUTS.goToDemandCalendar.path
      ? { ...shortcut, path: HELIX_DEMAND_CALENDAR_PATH }
      : shortcut,
  )
}

/** `SEQUENCE_BY_PATH` with the same Helix remap applied, for rendering shortcut badges. */
export function sequenceByPathFor({
  showHelix,
}: {
  showHelix: boolean
}): Record<string, readonly Hotkey[]> {
  if (!showHelix) return SEQUENCE_BY_PATH
  return Object.fromEntries(navigationShortcutsFor({ showHelix }).map((s) => [s.path, s.sequence]))
}
